import { useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { FileText, UploadCloud } from 'lucide-react'
import { Button, Chip, Drawer } from '@/components/ui'
import { usePastePriceSheet, useUploadPriceSheet, useVendorPriceSheetIntakes } from '../api/queries'
import type { IntakeStatus } from '../api/types'

const ACCEPT_ATTR =
  '.pdf,.PDF,.xlsx,.XLSX,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export interface VendorPriceSheetImportDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vendorId: number
  vendorName: string
}

const IN_FLIGHT: IntakeStatus[] = ['pending_extraction', 'extraction_running']

const STATUS_LABEL: Record<IntakeStatus, string> = {
  pending_extraction: 'Queued',
  extraction_running: 'Extracting…',
  extraction_complete: 'Matching…',
  review: 'Ready for review',
  applied: 'Applied',
  rejected: 'Rejected',
  failed: 'Failed',
}

/**
 * "Import price sheet" drawer on the Vendor Pricelists detail page
 * (Sprint 38). Two panes:
 *   1. Upload/paste + recent imports for this vendor.
 *   2. Review — polls the intake while extraction runs, then renders a
 *      diff table (current vs. parsed price) with per-line apply/skip.
 *
 * Non-modal, mirrors `SalesIntakeUploadDrawer`'s shape but scoped to a
 * single vendor and a single in-flight intake at a time (no multi-file
 * queue — a price sheet is one document per import).
 */
export function VendorPriceSheetImportDrawer({
  open,
  onOpenChange,
  vendorId,
  vendorName,
}: VendorPriceSheetImportDrawerProps) {
  const navigate = useNavigate()
  const [pasteMode, setPasteMode] = useState(false)
  const [pasteText, setPasteText] = useState('')
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const recent = useVendorPriceSheetIntakes(open ? vendorId : null)
  const upload = useUploadPriceSheet()
  const paste = usePastePriceSheet()

  // Review happens on its own page — a price sheet can run to hundreds of
  // lines, each of which may need a decision.
  function startIntake(id: string) {
    setPasteMode(false)
    setPasteText('')
    onOpenChange(false)
    void navigate({
      to: '/vendor-pricelists/$vendorId/imports/$intakeId',
      params: { vendorId: String(vendorId), intakeId: id },
    } as never)
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    upload.mutate({ vendorId, file }, { onSuccess: (res) => startIntake(res.intake_id) })
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    const file = e.dataTransfer?.files?.[0]
    if (!file) return
    upload.mutate({ vendorId, file }, { onSuccess: (res) => startIntake(res.intake_id) })
  }

  function submitPaste() {
    if (!pasteText.trim()) return
    paste.mutate(
      { vendorId, text: pasteText.trim() },
      { onSuccess: (res) => startIntake(res.intake_id) },
    )
  }

  function close() {
    setPasteMode(false)
    setPasteText('')
    onOpenChange(false)
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      size="wide"
      title={`Import price sheet — ${vendorName}`}
      meta="Upload a PDF/XLSX price sheet or paste the text. CARLA extracts the lines and matches them against this vendor's Odoo prices; you review them on their own page before anything reaches Odoo."
      footer={
        <div className="flex w-full items-center justify-end">
          <Button variant="secondary" size="sm" onClick={close}>
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          className="flex flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed border-border-default bg-surface-sunk px-6 py-10 text-center"
        >
          <UploadCloud size={28} className="text-tertiary" />
          <p className="m-0 text-sm text-secondary">Drag &amp; drop a price sheet here</p>
          <p className="m-0 text-xs text-tertiary">or</p>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            className="hidden"
            onChange={onPick}
          />
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={upload.isPending}
            >
              {upload.isPending ? 'Uploading…' : 'Choose file'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setPasteMode((v) => !v)}>
              Paste text instead
            </Button>
          </div>
          <p className="m-0 text-xs text-tertiary">PDF or XLSX — up to 25 MB</p>
          {upload.isError ? (
            <p className="m-0 text-xs text-danger">{(upload.error as Error).message}</p>
          ) : null}
        </div>

        {pasteMode ? (
          <div className="flex flex-col gap-2 rounded-sm border border-border-default bg-surface p-3">
            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="Paste the price-sheet text (e.g. copied from a vendor email)…"
              rows={8}
              className="w-full resize-none rounded-xs border border-border-default bg-surface-sunk p-2 text-sm text-primary"
            />
            <div className="flex items-center justify-end gap-2">
              {paste.isError ? (
                <span className="text-xs text-danger">{(paste.error as Error).message}</span>
              ) : null}
              <Button
                variant="primary"
                size="sm"
                onClick={submitPaste}
                disabled={paste.isPending || !pasteText.trim()}
              >
                {paste.isPending ? 'Submitting…' : 'Submit'}
              </Button>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <span className="text-role-section-label">Recent imports</span>
          {recent.data && recent.data.intakes.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {recent.data.intakes.map((intake) => (
                <li key={intake.id}>
                  <button
                    type="button"
                    onClick={() => startIntake(intake.id)}
                    className="flex w-full cursor-pointer items-center gap-3 rounded-xs border border-border-subtle bg-surface px-3 py-2 text-left text-sm hover:bg-surface-hover"
                  >
                    <FileText size={14} className="text-tertiary" />
                    <span className="flex-1 truncate text-secondary">
                      {intake.created_at ? new Date(intake.created_at).toLocaleString() : '—'}
                      {intake.line_count != null ? ` · ${intake.line_count} line(s)` : ''}
                    </span>
                    <StatusChip status={intake.status} scheduledCount={intake.scheduled_count ?? 0} effectiveDate={intake.effective_date} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 text-sm text-tertiary">No imports yet for this vendor.</p>
          )}
        </div>
      </div>
    </Drawer>
  )
}

function scheduledLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function StatusChip({
  status,
  scheduledCount = 0,
  effectiveDate = null,
}: {
  status: IntakeStatus
  scheduledCount?: number
  effectiveDate?: string | null
}) {
  if (status === 'review' && scheduledCount > 0 && effectiveDate) {
    return (
      <Chip size="xs" variant="info" dot>
        {scheduledCount} scheduled for {scheduledLabel(effectiveDate)}
      </Chip>
    )
  }
  const variant: 'info' | 'success' | 'danger' | 'neutral' =
    status === 'applied'
      ? 'success'
      : status === 'failed' || status === 'rejected'
        ? 'danger'
        : IN_FLIGHT.includes(status)
          ? 'info'
          : 'neutral'
  return (
    <Chip size="xs" variant={variant} dot>
      {STATUS_LABEL[status]}
    </Chip>
  )
}
